import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DexScreenerModal from '../components/DexScreenerModal';

export default function Milestones() {
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    multiplier: '',
    chain: ''
  });
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [expandedTokens, setExpandedTokens] = useState(new Set());
  const [chartModal, setChartModal] = useState({ isOpen: false, chain: '', tokenAddress: '', tokenSymbol: '' });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(text);
      setTimeout(() => setCopiedAddress(null), 2000);
    });
  };

  useEffect(() => {
    fetchMilestones();

    // Auto-refresh every 1 minute
    const refreshInterval = setInterval(() => {
      fetchMilestones();
    }, 60000);

    return () => clearInterval(refreshInterval);
  }, [filters]);

  const fetchMilestones = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (filters.multiplier) params.append('multiplier', filters.multiplier);
      if (filters.chain) params.append('chain', filters.chain);
      params.append('limit', '50');

      const response = await api.get(`/analytics/milestones?${params.toString()}`);
      setMilestones(response.data.milestones);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching milestones:', err);
      setError('Failed to load milestones');
    } finally {
      setLoading(false);
    }
  };

  const formatMarketCap = (mc) => {
    if (!mc && mc !== 0) return 'N/A';
    if (mc >= 1000000000) return `$${(mc / 1000000000).toFixed(2)}B`;
    if (mc >= 1000000) return `$${(mc / 1000000).toFixed(2)}M`;
    if (mc >= 1000) return `$${(mc / 1000).toFixed(2)}K`;
    return `$${mc.toFixed(0)}`;
  };

  const getChainEmoji = (chain) => {
    const emojis = {
      solana: '◎',
      base: '🔵',
      ethereum: '⟠',
      bsc: '🟡'
    };
    return emojis[chain] || '⛓️';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const generateTradeUrl = (chain, address) => {
    // Axiom supports Solana and BSC
    if (chain === 'solana' || chain === 'bsc') {
      return `https://axiom.trade/t/${address}`;
    }
    // GMGN.ai supports Base and Ethereum
    if (chain === 'base') {
      return `https://gmgn.ai/base/token/${address}`;
    }
    if (chain === 'ethereum') {
      return `https://gmgn.ai/eth/token/${address}`;
    }
    return null;
  };

  const getTradeButtonLabel = (chain) => {
    if (chain === 'solana' || chain === 'bsc') {
      return 'Axiom';
    }
    if (chain === 'base' || chain === 'ethereum') {
      return 'GMGN';
    }
    return 'Trade';
  };

  const getFomoUrl = (chain, address) => {
    const slug = { solana: 'solana', ethereum: 'ethereum', base: 'base', bsc: 'bnb' }[chain];
    return slug && address ? `https://fomo.family/tokens/${slug}/${address}` : null;
  };

  // Chart URL generators
  const getAxiomChartUrl = (chain, address) => {
    if (chain === 'solana' || chain === 'bsc') {
      return `https://axiom.trade/t/${address}`;
    }
    return null;
  };

  const getDexScreenerUrl = (chain, address) => {
    const chainMap = {
      solana: 'solana',
      base: 'base',
      ethereum: 'ethereum',
      bsc: 'bsc'
    };
    return `https://dexscreener.com/${chainMap[chain] || chain}/${address}`;
  };

  const getGmgnUrl = (chain, address) => {
    if (chain === 'base') {
      return `https://gmgn.ai/base/token/${address}`;
    }
    if (chain === 'ethereum') {
      return `https://gmgn.ai/eth/token/${address}`;
    }
    if (chain === 'solana') {
      return `https://gmgn.ai/sol/token/${address}`;
    }
    return null;
  };

  const toggleTokenExpansion = (tokenId) => {
    const newExpanded = new Set(expandedTokens);
    if (newExpanded.has(tokenId)) {
      newExpanded.delete(tokenId);
    } else {
      newExpanded.add(tokenId);
    }
    setExpandedTokens(newExpanded);
  };

  const sortedMultiplierStats = useMemo(
    () => [...(stats?.byMultiplier || [])].sort((a, b) => Number(a._id) - Number(b._id)),
    [stats?.byMultiplier]
  );

  const filterMultiplierOptions = useMemo(
    () => sortedMultiplierStats.filter((s) => Number(s._id) <= 100),
    [sortedMultiplierStats]
  );

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Milestone Achievements</h1>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading milestones...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">🎯 Milestone Achievements</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Stats Overview - Compact Layout */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Main Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {sortedMultiplierStats.reduce((sum, s) => sum + s.count, 0)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{stats.recent24h}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">24h</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600 capitalize">
                  {stats.byChain[0]?._id || 'N/A'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Top Chain</div>
              </div>
            </div>
          </div>

          {/* Chain Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">By Chain</div>
            <div className="flex flex-wrap gap-2">
              {stats.byChain.map((chain) => (
                <span key={chain._id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                  {getChainEmoji(chain._id)} {chain._id}: {chain.count}
                </span>
              ))}
            </div>
          </div>

          {/* Multiplier summary — top tiers only */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Top Multipliers
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs">
              {sortedMultiplierStats
                .filter((s) => Number(s._id) <= 100)
                .map((stat) => (
                  <div key={stat._id} className="flex justify-between text-gray-700 dark:text-gray-300">
                    <span className="font-medium text-purple-700 dark:text-purple-400">{stat._id}x</span>
                    <span className="text-gray-500 dark:text-gray-400">{stat.count}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Full multiplier breakdown — compact scrollable table */}
      {stats && sortedMultiplierStats.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
              By Multiplier ({sortedMultiplierStats.length} tiers)
            </div>
            <div className="text-[10px] text-gray-400">sorted low → high</div>
          </div>
          <div className="max-h-36 overflow-y-auto border border-gray-100 dark:border-gray-700 rounded-md">
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-0 divide-x divide-y divide-gray-100 dark:divide-gray-700">
              {sortedMultiplierStats.map((stat) => (
                <button
                  key={stat._id}
                  type="button"
                  onClick={() => setFilters({ ...filters, multiplier: String(stat._id) })}
                  className={`px-2 py-1.5 text-left text-[11px] hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors ${
                    filters.multiplier === String(stat._id) ? 'bg-purple-100 dark:bg-purple-900/30' : ''
                  }`}
                  title={`Filter ${stat._id}x milestones`}
                >
                  <span className="font-semibold text-purple-700 dark:text-purple-400">{stat._id}x</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-1">{stat.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Multiplier
            </label>
            <select
              value={filters.multiplier}
              onChange={(e) => setFilters({ ...filters, multiplier: e.target.value })}
              className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
            >
              <option value="">All</option>
              {filterMultiplierOptions.map((s) => (
                <option key={s._id} value={s._id}>{s._id}x ({s.count})</option>
              ))}
              {sortedMultiplierStats.some((s) => Number(s._id) > 100) && (
                <optgroup label="100x+">
                  {sortedMultiplierStats.filter((s) => Number(s._id) > 100).map((s) => (
                    <option key={s._id} value={s._id}>{s._id}x ({s.count})</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Chain
            </label>
            <select
              value={filters.chain}
              onChange={(e) => setFilters({ ...filters, chain: e.target.value })}
              className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
            >
              <option value="">All</option>
              <option value="solana">Solana</option>
              <option value="base">Base</option>
              <option value="ethereum">Ethereum</option>
              <option value="bsc">BSC</option>
            </select>
          </div>

          {(filters.multiplier || filters.chain) && (
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ multiplier: '', chain: '' })}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-100"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Milestones List - Token-centric view */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Token
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Highest Milestone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Total Milestones
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                IMC
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Latest Market Cap
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Last Updated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {milestones.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  No milestones found
                </td>
              </tr>
            ) : (
              milestones.map((token) => {
                // Ensure milestones array exists
                const tokenMilestones = token.milestones || [];

                return (
                <React.Fragment key={token._id}>
                  {/* Main token row */}
                  <tr className="hover:bg-gray-50 dark:bg-gray-800/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="text-lg mr-2">{getChainEmoji(token.chain)}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/token/${token.chain}/${token.tokenAddress}`)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {token.tokenSymbol}
                            </button>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {token.tokenName}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                            {token.tokenAddress.substring(0, 12)}...{token.tokenAddress.slice(-8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
                        {token.highestMultiplier}x
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {tokenMilestones.length}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="font-medium text-gray-700 dark:text-gray-200">
                        {formatMarketCap(token.initialMarketCap)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="font-medium text-green-600">
                        {formatMarketCap(token.latestMarketCap)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(token.lastUpdatedAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {getFomoUrl(token.chain, token.tokenAddress) && (
                          <a
                            href={getFomoUrl(token.chain, token.tokenAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                            title="View on Fomo"
                          >
                            FOMO
                          </a>
                        )}
                        {getAxiomChartUrl(token.chain, token.tokenAddress) && (
                          <a
                            href={getAxiomChartUrl(token.chain, token.tokenAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                            title="View on Axiom"
                          >
                            AXI
                          </a>
                        )}
                        <a
                          href={getDexScreenerUrl(token.chain, token.tokenAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                          title="View on DexScreener"
                        >
                          DEX
                        </a>
                        {getGmgnUrl(token.chain, token.tokenAddress) && (
                          <a
                            href={getGmgnUrl(token.chain, token.tokenAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                            title="View on GMGN"
                          >
                            GMGN
                          </a>
                        )}
                        <button
                          onClick={() => setChartModal({
                            isOpen: true,
                            chain: token.chain,
                            tokenAddress: token.tokenAddress,
                            tokenSymbol: token.tokenSymbol
                          })}
                          className="text-xs px-2 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                          title="Open Chart Preview"
                        >
                          Chart
                        </button>
                        <button
                          onClick={() => copyToClipboard(token.tokenAddress)}
                          className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                          title="Copy contract address"
                        >
                          {copiedAddress === token.tokenAddress ? '✓' : '📋'}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleTokenExpansion(token._id)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        {expandedTokens.has(token._id) ? '▼ Hide' : '▶ Show'}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded milestone details */}
                  {expandedTokens.has(token._id) && tokenMilestones.length > 0 && (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50">
                        <div className="space-y-2">
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                            All Milestones for {token.tokenSymbol}:
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {tokenMilestones
                              .sort((a, b) => a.multiplier - b.multiplier)
                              .map((milestone, idx) => (
                              <div key={idx} className="bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
                                <div className="flex justify-between items-start mb-2">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                                    {milestone.multiplier}x
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDate(milestone.achievedAt)}
                                  </span>
                                </div>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Initial:</span>
                                    <span className="font-medium">
                                      {formatMarketCap(milestone.initialMarketCap)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">At Milestone:</span>
                                    <span className="font-medium text-green-600">
                                      {formatMarketCap(milestone.currentMarketCap)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Gain:</span>
                                    <span className="font-bold text-green-600">
                                      +{milestone.percentageGain.toFixed(2)}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* DexScreener Chart Modal */}
      <DexScreenerModal
        isOpen={chartModal.isOpen}
        onClose={() => setChartModal({ ...chartModal, isOpen: false })}
        chain={chartModal.chain}
        tokenAddress={chartModal.tokenAddress}
        tokenSymbol={chartModal.tokenSymbol}
      />
    </div>
  );
}
